import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Quote, ShieldAlert } from 'lucide-react';

const Mission = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 15 },
    visible: { 
      opacity: 1, 
      y: 0, 
      transition: { duration: 0.5, ease: "easeOut" } 
    },
  };

  const engagements = [
    { t: "Transparence", d: "Rapports techniques clairs et exploitables." },
    { t: "Identification", d: "Détection des vulnérabilités avant exploitation." },
    { t: "Scénarios Réels", d: "Méthodes basées sur des schémas d’attaque réels." },
    { t: "Confidentialité", d: "Protocoles stricts de traitement des données." },
    { t: "Accompagnement", d: "Plans de remédiation concrets et priorisés." }
  ];

  return (
    <section className="w-full bg-[#F8FAFC] py-16 lg:py-24 px-6 overflow-hidden font-inter">
      <div className="max-w-[1050px] mx-auto"> 
        <div className="flex flex-col lg:flex-row items-center justify-between gap-12 lg:gap-20">
          
          {/* CÔTÉ GAUCHE - STRATÉGIE & ENGAGEMENTS */}
          <motion.div 
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            className="w-full lg:w-[58%] flex flex-col"
          >
            <motion.div variants={itemVariants} className="flex items-center gap-2 mb-4">
               <span className="text-[#3b82f6] text-[11px] font-black uppercase tracking-[0.2em]">
                Notre Mission
              </span>
              <div className="h-[1px] w-8 bg-blue-200" />
            </motion.div>

            <motion.h2 
              variants={itemVariants}
              className="text-[#0f172a] text-[30px] md:text-[38px] font-[900] leading-[1.15] mb-6 tracking-tight"
            >
              Réduire votre surface d'attaque face aux menaces invisibles.
            </motion.h2>
            
            <motion.div 
              variants={itemVariants}
              className="space-y-4 text-slate-600 text-[14.5px] md:text-[15px] leading-relaxed mb-10 font-medium opacity-90 max-w-[540px]"
            >
              <p>
                Chez Nodixs, nous considérons la cybersécurité comme un rempart dynamique. 
                <span className="text-[#0f172a] font-bold"> Une organisation peut être compromise sans même en avoir conscience</span> : fuites de données, mots de passe exposés ou mauvaises configurations.
              </p>
              <p className="border-l-2 border-blue-500 pl-4 bg-blue-50/50 py-1">
                Notre objectif est d'identifier ces angles morts, d'analyser les risques réels et de neutraliser vos expositions avant qu'elles ne soient exploitées.
              </p>
            </motion.div>

            {/* ENGAGEMENTS - Plus d'autorité */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-5 gap-x-4">
              {engagements.map((item, i) => (
                <motion.div key={i} variants={itemVariants} className="flex items-start gap-3 group">
                  <div className="mt-1 bg-white shadow-sm p-1.5 rounded-lg group-hover:bg-[#3b82f6] transition-all duration-300 border border-slate-100">
                    <CheckCircle2 className="text-[#3b82f6] group-hover:text-white" size={14} />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-[#0f172a] font-black text-[13.5px] tracking-tight">{item.t}</span>
                    <span className="text-slate-500 text-[12px] font-medium leading-tight">{item.d}</span>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* CÔTÉ DROIT - CRÉDIBILITÉ & PREUVE */}
          <motion.div 
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="w-full lg:w-[38%] relative"
          >
            {/* Décoration subtile */}
            <div className="absolute -top-10 -right-10 w-32 h-32 bg-blue-400/10 rounded-full blur-3xl" />
            
            <div className="relative z-10 bg-[#0f172a] p-8 md:p-10 rounded-[2rem] shadow-2xl shadow-blue-900/20 border border-white/5">
              <Quote className="absolute top-6 right-8 text-blue-500/20 w-10 h-10" />
              
              <div className="flex items-center gap-2 mb-6 text-blue-400">
                <ShieldAlert size={18} />
                <span className="text-[10px] font-black uppercase tracking-[0.2em]">Impact Réel</span>
              </div>

              <p className="text-blue-50 text-[15.5px] md:text-[17px] font-medium leading-relaxed italic mb-8 relative z-10">
                "L'expertise de Nodixs nous a permis de découvrir des failles critiques que nos outils de scan automatiques n'avaient jamais détectées."
              </p>
              
              <div className="flex items-center gap-4">
                <div className="h-10 w-10 rounded-full bg-gradient-to-tr from-blue-600 to-blue-400 flex items-center justify-center text-white font-bold text-xs">
                  IT
                </div>
                <div className="flex flex-col">
                  <span className="text-white font-bold text-[13px]">Responsable Infrastructures</span>
                  <span className="text-blue-400/70 text-[11px] font-bold uppercase tracking-wider">Secteur Industriel</span>
                </div>
              </div>
            </div>

            {/* Badge de réassurance sous le bloc */}
            <div className="mt-6 flex justify-center lg:justify-start">
              <div className="px-4 py-2 bg-white rounded-full border border-slate-100 shadow-sm flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                <span className="text-[11px] font-bold text-slate-500 uppercase tracking-tight">Disponibilité : Audits sur demande</span>
              </div>
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
};

export default Mission;